import { useState, useEffect } from 'react';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Carousel images - completed project photos
  const carouselImages = [
    {
      url: 'https://public.readdy.ai/ai/img_res/32c32b3de233e33ac81888d72b27f7be.jpg',
      alt: 'Luxury bathroom remodeling project Detroit'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=beautiful%20finished%20basement%20renovation%20with%20custom%20built-in%20entertainment%20center%20comfortable%20seating%20area%20recessed%20lighting%20luxury%20vinyl%20plank%20flooring%20modern%20contemporary%20design%20completed%20project%20professional%20interior%20photography&width=1920&height=1080&seq=hero-carousel-basement-002&orientation=landscape',
      alt: 'Finished basement remodeling Michigan'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=elegant%20spa-like%20master%20bathroom%20with%20walk-in%20shower%20frameless%20glass%20natural%20stone%20tile%20floating%20vanity%20modern%20fixtures%20sophisticated%20lighting%20completed%20renovation%20professional%20interior%20photography&width=1920&height=1080&seq=hero-carousel-bathroom-003&orientation=landscape',
      alt: 'Master bathroom renovation Southeast Michigan'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=transformed%20basement%20living%20space%20with%20wet%20bar%20kitchenette%20custom%20cabinetry%20stone%20countertops%20pendant%20lighting%20cozy%20entertainment%20area%20completed%20remodeling%20project%20professional%20interior%20photography&width=1920&height=1080&seq=hero-carousel-basement-004&orientation=landscape',
      alt: 'Basement bar remodeling Detroit area'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=luxurious%20contemporary%20bathroom%20remodel%20with%20large%20format%20porcelain%20tiles%20heated%20floors%20backlit%20mirror%20dual%20shower%20heads%20premium%20finishes%20completed%20project%20professional%20interior%20photography&width=1920&height=1080&seq=hero-carousel-bathroom-005&orientation=landscape',
      alt: 'Contemporary bathroom remodel Michigan'
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-advance carousel every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [carouselImages.length]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  return (
    <section id="home" className="relative min-h-screen">

      {/* ── Sticky Nav (links only, no logo) ── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white shadow-md' : 'bg-black/20 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? 'py-3' : 'py-4'}`}>

            {/* Logo — only visible when scrolled */}
            <div className={`transition-all duration-500 overflow-hidden ${scrolled ? 'opacity-100 max-w-[120px] sm:max-w-[180px]' : 'opacity-0 max-w-0'}`}>
              <img
                src="https://public.readdy.ai/ai/img_res/99d478ee-51ff-4727-81ec-e4279c3414e4.png"
                alt="American Dream Remodeling INC"
                className="h-8 sm:h-12 w-auto object-contain"
              />
            </div>

            {/* Nav Links — centered when not scrolled, pushed right of logo when scrolled */}
            <div className={`hidden md:flex items-center gap-5 lg:gap-7 transition-all duration-300 ${scrolled ? '' : 'absolute left-1/2 -translate-x-1/2'}`}>
              {['home','services','why-choose-us','gallery','service-areas','contact'].map((id) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap cursor-pointer capitalize ${scrolled ? 'text-slate-700 hover:text-slate-900' : 'text-white/90 hover:text-white'}`}
                >
                  {id === 'why-choose-us' ? 'Why Choose Us' : id === 'service-areas' ? 'Service Areas' : id.charAt(0).toUpperCase() + id.slice(1)}
                </button>
              ))}
            </div>

            {/* CTA Phone — Desktop */}
            <a
              href="tel:586-443-0294"
              className={`hidden md:inline-flex items-center gap-2 px-4 lg:px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${scrolled ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-white/15 border border-white/40 text-white hover:bg-white/25'}`}
            >
              <i className="ri-phone-line"></i>
              <span className="hidden lg:inline">586-443-0294</span>
              <span className="lg:hidden">Call</span>
            </a>

            {/* Hamburger Menu Button — Mobile */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${scrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-white hover:bg-white/10'}`}
              aria-label="Toggle menu"
            >
              <i className={`text-2xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu Drawer */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ${mobileMenuOpen ? 'max-h-screen' : 'max-h-0'}`}>
          <div className={`${scrolled ? 'bg-white' : 'bg-black/90 backdrop-blur-md'} border-t ${scrolled ? 'border-slate-200' : 'border-white/10'}`}>
            <div className="px-4 py-4 space-y-1">
              {['home','services','why-choose-us','gallery','service-areas','contact'].map((id) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer capitalize ${scrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-white hover:bg-white/10'}`}
                >
                  {id === 'why-choose-us' ? 'Why Choose Us' : id === 'service-areas' ? 'Service Areas' : id.charAt(0).toUpperCase() + id.slice(1)}
                </button>
              ))}
              <a
                href="tel:586-443-0294"
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-colors cursor-pointer mt-2 ${scrolled ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-white/15 border border-white/40 text-white hover:bg-white/25'}`}
              >
                <i className="ri-phone-line"></i>
                586-443-0294
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Full-Viewport Hero with Carousel ── */}
      <div className="relative min-h-screen flex flex-col items-center justify-center">

        {/* Carousel Background */}
        <div className="absolute inset-0">
          {carouselImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover object-center"
              />
            </div>
          ))}
          {/* Multi-layer overlay for depth */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/75"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30"></div>
        </div>

        {/* Carousel Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-full transition-all border border-white/30 cursor-pointer group"
          aria-label="Previous slide"
        >
          <i className="ri-arrow-left-s-line text-2xl text-white group-hover:scale-110 transition-transform"></i>
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-full transition-all border border-white/30 cursor-pointer group"
          aria-label="Next slide"
        >
          <i className="ri-arrow-right-s-line text-2xl text-white group-hover:scale-110 transition-transform"></i>
        </button>

        {/* Carousel Dot Indicators */}
        <div className="absolute bottom-20 sm:bottom-24 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          {carouselImages.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all cursor-pointer ${index === currentSlide ? 'w-8 h-2 bg-teal-400' : 'w-2 h-2 bg-white/40 hover:bg-white/60'} rounded-full`}
              aria-label={`Go to slide ${index + 1}`}
            ></button>
          ))}
        </div>

        {/* ── Centered Brand Content ── */}
        <div className="relative z-10 flex flex-col items-center text-center px-4 sm:px-6 w-full">

          {/* Eyebrow */}
          <div className="mb-6 sm:mb-8">
            <span className="inline-block text-teal-300 text-xs font-bold tracking-[0.2em] sm:tracking-[0.25em] uppercase border border-teal-400/40 px-3 sm:px-4 py-1.5 rounded-full bg-teal-900/20 backdrop-blur-sm">
              Detroit's Premier Remodeling Company
            </span>
          </div>

          {/* ── LARGE CENTERED LOGO — responsive sizing ── */}
          <div className="w-full flex items-center justify-center mb-8 sm:mb-10">
            <img
              src="https://public.readdy.ai/ai/img_res/99d478ee-51ff-4727-81ec-e4279c3414e4.png"
              alt="American Dream Remodeling INC Logo"
              className="w-[85vw] sm:w-[70vw] md:w-[60vw] max-w-[700px]"
              style={{
                height: 'auto',
                objectFit: 'contain',
                filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.5))',
              }}
            />
          </div>

          {/* Thin gold divider */}
          <div className="flex items-center gap-3 sm:gap-4 mb-8 sm:mb-10 w-full max-w-md">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent to-teal-400/60"></div>
            <i className="ri-diamond-line text-teal-400 text-sm"></i>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent to-teal-400/60"></div>
          </div>

          {/* Headline — responsive font sizing */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-5 leading-tight tracking-tight max-w-4xl px-2">
            Bathroom &amp; Basement Remodeling<br />
            <span className="text-teal-300">Crafted for Michigan Homes</span>
          </h1>

          {/* Subtext */}
          <p className="text-base sm:text-lg text-white/75 mb-8 sm:mb-10 max-w-2xl leading-relaxed font-light px-2">
            Expert craftsmanship, honest pricing, and transformations that exceed expectations — serving Southeast Michigan for over a decade.
          </p>

          {/* CTAs — stack on mobile, row on desktop */}
          <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 mb-10 sm:mb-14 w-full px-2">
            <a
              href="tel:586-443-0294"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm font-semibold hover:bg-teal-500 transition-all hover:scale-105 shadow-xl shadow-teal-900/40 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-base"></i>
              Call Now — 586-443-0294
            </a>
            <button
              onClick={() => scrollToSection('estimate')}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm font-semibold hover:bg-white/20 transition-all border border-white/30 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-list-3-line text-base"></i>
              Get Free Estimate
            </button>
            <button
              onClick={() => scrollToSection('estimate')}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm font-semibold hover:bg-teal-50 transition-all hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-check-line text-base"></i>
              Book a Consultation
            </button>
          </div>

          {/* Trust Badges — wrap on mobile, hide dividers */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-3 sm:gap-x-6 lg:gap-x-8 max-w-xl mx-auto">
            <div className="flex items-center gap-2 text-white/80">
              <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center">
                <i className="ri-shield-check-line text-teal-400 text-lg sm:text-xl"></i>
              </div>
              <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Fully Insured</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center">
                <i className="ri-award-line text-teal-400 text-lg sm:text-xl"></i>
              </div>
              <span className="text-xs sm:text-sm font-medium whitespace-nowrap">10+ Years Experience</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center">
                <i className="ri-star-fill text-teal-400 text-lg sm:text-xl"></i>
              </div>
              <span className="text-xs sm:text-sm font-medium whitespace-nowrap">100% Satisfaction</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center">
                <i className="ri-map-pin-line text-teal-400 text-lg sm:text-xl"></i>
              </div>
              <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Southeast Michigan</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}